import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Button,
  Form,
  FloatingLabel,
  Spinner,
  Container,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft } from "react-feather";

import { useNotification } from "../../components/notification";
import apiService from "../../services/apiService";

// Define validation schema using yup
const schema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup
    .string()
    .required("Phone is required")
    .matches(/^\d{10}$/, "Phone must be 10 digits"),
  role: yup.string().required("Role is required"),
  isActive: yup.boolean().required("Status is required"),
});

const CreateUser = () => {
  const didFetch = useRef(false);
  const { userId } = useParams(); // Get user ID from URL (if editing)
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]); // List of roles

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
      isActive: true,
    },
  });

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await apiService.get("/role?isDropdown=true");
      setRoles(response.filter((role) => role.value !== "all"));
    } catch (error) {
      showNotification("danger", "Failed to fetch roles.");
    }
  }, [showNotification]);

  // Fetch user details if in edit mode
  useEffect(() => {
    if (didFetch.current) return; // Prevent second call
    didFetch.current = true;

    fetchRoles();
    if (userId) {
      const fetchUserDetails = async () => {
        setIsLoading(true);
        try {
          const response = await apiService.get(`/users/${userId}`);
          reset(response); // Pre-populate the form with fetched data
        } catch (error) {
          showNotification("danger", error || "Failed to fetch user details.");
        } finally {
          setIsLoading(false);
        }
      };

      fetchUserDetails();
    }
  }, [userId, reset, showNotification, fetchRoles]);

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      if (userId) {
        // Update existing user
        await apiService.put(`/users/${userId}`, data);
        showNotification("success", "User updated successfully!", 5000);
      } else {
        // Create new user
        await apiService.post("/users", data);
        showNotification("success", "User created successfully!", 5000);
      }
      navigate("/users"); // Redirect to the manage users page
    } catch (error) {
      showNotification("danger", error || "Failed to save user.", 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container fluid className="p-0">
      <Card className="p-4 border-0">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h1 className="mb-0 fs-5">{userId ? "Edit User" : "Add User"}</h1>
            <Link variant="light" to="/users" className="btn btn-light">
              <ArrowLeft size={18} /> Back
            </Link>
          </div>
          <p className="mb-0 text-light-emphasis fw-light">
            {userId
              ? "Update the user details below."
              : "Please enter the user details below."}
          </p>
          <hr className="mt-3 mb-4" />
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              {/* First Name */}
              <Col xs={12} md={6} lg={4}>
                <FloatingLabel
                  controlId="txtFirstName"
                  label="First Name"
                  className="mb-4"
                >
                  <Form.Control
                    type="text"
                    placeholder="First Name"
                    {...register("firstName")}
                    isInvalid={!!errors.firstName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Last Name */}
              <Col xs={12} md={6} lg={4}>
                <FloatingLabel
                  controlId="txtLastName"
                  label="Last Name"
                  className="mb-4"
                >
                  <Form.Control
                    type="text"
                    placeholder="Last Name"
                    {...register("lastName")}
                    isInvalid={!!errors.lastName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Email */}
              <Col xs={12} md={6} lg={4}>
                <FloatingLabel
                  controlId="txtEmail"
                  label="Email"
                  className="mb-4"
                >
                  <Form.Control
                    type="email"
                    placeholder="Email"
                    {...register("email")}
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Phone */}
              <Col xs={12} md={6} lg={4}>
                <FloatingLabel
                  controlId="txtPhone"
                  label="Phone"
                  className="mb-4"
                >
                  <Form.Control
                    type="text"
                    placeholder="Phone"
                    {...register("phone")}
                    isInvalid={!!errors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Role Dropdown */}
              <Col xs={12} md={6} lg={4}>
                <FloatingLabel controlId="ddRole" label="Role" className="mb-4">
                  <Form.Select {...register("role")} isInvalid={!!errors.role}>
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.role?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col
                xs={12}
                md={6}
                lg={4}
                className="mb-4 d-flex align-items-center"
              >
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type="switch"
                      id="chkStatus"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      label="Allow Access"
                    />
                  )}
                />
              </Col>
            </Row>

            <div>
              <Button
                variant="primary"
                disabled={isLoading}
                type="submit"
                className="btn-main"
              >
                {isLoading ? (
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                ) : userId ? (
                  "Update User"
                ) : (
                  "Add User"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CreateUser;
